import React, { useEffect, useMemo, useState } from "react";
import {
  MarketplaceSection,
  InnerContainer,
  SectionTitle,
  HighlightCard,
  HighlightHeader,
  HighlightLabel,
  HighlightPill,
  HighlightGrid,
  CardBase,
  ResellerSection,
  ResellerTitle,
  ResellerGrid,
  ResellerCardRoot,
  ResellerHeader,
  ResellerLeft,
  ResellerAvatar,
  ResellerName,
  ResellerDescription,
  ResellerBadge,
  ResellerStats,
  ResellerStatBox,
  StatValue,
  StatLabel,
  PrimaryButton,
  SidebarOverlay,
  SidebarPanel,
  SidebarHeader,
  SidebarTitle,
  SidebarClose,
  SidebarList,
  SidebarStoreCard,
  MoreStoresCard,
  MoreStoresInner,
  MoreStoresNumber,
  MoreStoresLabel,
  MoreStoresLink,
  MoreTendersCard,
  MoreTendersInner,
  MoreTendersNumber,
  MoreTendersLabel,
  MoreTendersLink,
  ProductsSection,
  ProductsTitle,
  ProductsGrid,
  ProductCardRoot,
} from "./Marketplace.styles";
import { getAuctionList, getPublicSellerStores } from "../../../../utils/services/user.service";

// --------- Static data for UI demo (can be wired to APIs later) ----------

const fallbackResellerItems = [
  {
    name: "Custodiy Real Estate Store",
    description: "Tokenized property listings & fractional investments.",
    products: 24,
    activeAuctions: 3,
    activeGroups: 5,
  },
  {
    name: "Pharma B2B Marketplace",
    description: "Wholesale pharma auctions and group procurement.",
    products: 46,
    activeAuctions: 6,
    activeGroups: 9,
  },
  {
    name: "Merchant Tech Store",
    description: "POS devices, API integrations and hardware tools.",
    products: 37,
    activeAuctions: 4,
    activeGroups: 8,
  },
  {
    name: "Luxury Watch Exchange",
    description: "High-end watches with escrow settlement.",
    products: 18,
    activeAuctions: 1,
    activeGroups: 2,
  },
  {
    name: "Energy Commodities Desk",
    description: "Structured deals on tokenized energy assets.",
    products: 14,
    activeAuctions: 2,
    activeGroups: 4,
  },
  {
    name: "Compute Power Exchange",
    description: "Tokenized CPU/GPU hours with SLA tracking.",
    products: 21,
    activeAuctions: 3,
    activeGroups: 7,
  },
];

const marketplaceItems = [
  {
    title: "Real Estate — Tokenized",
    subtitle: "Fractional ownership • rental yield",
    badge: "New",
  },
  {
    title: "Boats & Yachts",
    subtitle: "Fractional charter • maintenance fund",
    badge: "Popular",
  },
  { title: "USDT Top‑up", subtitle: "Instant balances", badge: "Popular" },
  { title: "CTY Staking", subtitle: "Flexible rewards", badge: "New" },
  { title: "Merchant POS", subtitle: "Widget + API", badge: "Pro" },
  { title: "On/Off‑Ramp", subtitle: "SEPA & SWIFT", badge: "Beta" },
  { title: "Custody (BitGo)", subtitle: "Multi‑sig vaults", badge: "Enterprise" },
  { title: "KYC (Veriff)", subtitle: "Automated identity", badge: "Verified" },
];

// const actionItems = [
//   {
//     title: "Flash Sale — Tokenized Asset",
//     subtitle: "Limited 48h allocation",
//     store: "Custodiy Real Estate Store",
//     status: "Active",
//     cta: "Participate",
//   },
//   {
//     title: "CTY Boost Campaign",
//     subtitle: "Staking multiplier x1.5",
//     store: "Compute Power Exchange",
//     status: "Paused",
//     cta: "View",
//   },
//   {
//     title: "Merchant Cashback Program",
//     subtitle: "Q1 incentive round",
//     store: "Merchant Tech Store",
//     status: "Closed",
//     cta: "Details",
//   },
// ];

// --------- Presentational sub‑components ----------

function ResellerCard({ item }) {
  const hasActivity = item.totalAuctions > 0 || item.totalGroups > 0;

  return (
    <ResellerCardRoot $highlight={hasActivity}>
      <ResellerHeader>
        <ResellerLeft>
          <ResellerAvatar>
            {item.name.split(" ")[0].slice(0, 2).toUpperCase()}
          </ResellerAvatar>
          <div>
            <ResellerName>{item.name}</ResellerName>
            <ResellerDescription>{item.description}</ResellerDescription>
          </div>
        </ResellerLeft>
        {hasActivity && (
          <ResellerBadge>
            {item.totalAuctions} Auctions • {item.totalGroups} Groups
          </ResellerBadge>
        )}
      </ResellerHeader>

      <ResellerStats>
        <ResellerStatBox>
          <StatValue>{item.products}</StatValue>
          <StatLabel>Products</StatLabel>
        </ResellerStatBox>
        <ResellerStatBox>
          <StatValue>{item.activeAuctions}</StatValue>
          <StatLabel>Active Auctions</StatLabel>
        </ResellerStatBox>
        <ResellerStatBox>
          <StatValue>{item.groups}</StatValue>
          <StatLabel>Groups</StatLabel>
        </ResellerStatBox>
      </ResellerStats>

      <PrimaryButton>View Store</PrimaryButton>
    </ResellerCardRoot>
  );
}

function ActionCard({ item }) {
  const statusColor =
    item.status === "Active"
      ? "#34d399"
      : item.status === "Paused"
        ? "#fbbf24"
        : "#fb7185";

  return (
    <CardBase
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.75rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600 }}>{item.title}</div>
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              marginTop: 4,
            }}
          >
            Store: {item.store}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#94a3b8",
            }}
          >
            {item.subtitle}
          </div>
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: statusColor,
          }}
        >
          {item.status}
        </span>
      </div>

      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <button
          style={{
            borderRadius: 999,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 800,
            background:
              "linear-gradient(135deg, #00f7d2 0%, #5ce9ff 100%)",
            color: "#020617",
            border: "none",
            cursor: "pointer",
          }}
        >
          {item.cta}
        </button>
      </div>
    </CardBase>
  );
}

function TenderCard({ item }) {
  console.log("🚀 ~ TenderCard ~ item:", item)
  // Status and countdown are driven by auction_start_time + auction_end_time
  const nowTs = Date.now();
  const initialStatus =
    item.deadlineTs <= nowTs
      ? "Expired"
      : item.startTs && nowTs < item.startTs
        ? "Upcoming"
        : "Open";

  const [status, setStatus] = useState(initialStatus);
  const [remain, setRemain] = useState(() => {
    const targetTs =
      initialStatus === "Upcoming" && item.startTs
        ? item.startTs
        : item.deadlineTs;
    return Math.max(
      0,
      Math.floor((targetTs - Date.now()) / 1000)
    );
  });

  // Total duration is from auction_start_time to auction_end_time, if available
  const totalDuration = React.useMemo(() => {
    if (item.startTs && item.startTs < item.deadlineTs) {
      return Math.max(
        1,
        Math.floor((item.deadlineTs - item.startTs) / 1000)
      );
    }
    // Fallback: 3 days window if start time is missing
    return 3 * 24 * 60 * 60;
  }, [item.startTs, item.deadlineTs]);

  React.useEffect(() => {
    if (status === "Expired") return;

    const getTargetTs = () =>
      status === "Upcoming" && item.startTs ? item.startTs : item.deadlineTs;

    const tick = () => {
      const targetTs = getTargetTs();
      if (!targetTs) return;

      const secondsLeft = Math.floor((targetTs - Date.now()) / 1000);

      if (secondsLeft <= 0) {
        if (status === "Upcoming") {
          // Switch to OPEN countdown when start time is reached
          const endSeconds = Math.floor(
            (item.deadlineTs - Date.now()) / 1000
          );
          setStatus("Open");
          setRemain(Math.max(0, endSeconds));
        } else if (status === "Open") {
          setRemain(0);
          setStatus("Expired");
        }
        return;
      }

      setRemain(secondsLeft);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [item.startTs, item.deadlineTs, status]);

  const d = Math.floor(remain / 86400);
  const h = Math.floor((remain % 86400) / 3600);
  const m = Math.floor((remain % 3600) / 60);
  const s = remain % 60;

  // Remaining percentage based on auction_start_time → auction_end_time
  const remainingRatio =
    totalDuration > 0 ? Math.max(0, Math.min(1, remain / totalDuration)) : 0;
  const remainingPercent = remainingRatio * 100;
  const endingSoon = remain > 0 && remain < 24 * 3600;

  const statusColor =
    status === "Open"
      ? "#34d399"
      : status === "Awarded"
        ? "#38bdf8"
        : "#fb7185";

  const handleAuthRedirect = () => {
    try {
      window.location.href = "/sign-in";
    } catch {
      // no-op in non-browser environments
    }
  };

  return (
    <CardBase>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.75rem",
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>{item.title}</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            Store: {item.store}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Budget: {item.budget}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Deadline: {item.deadline}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Bids: {item.bids}
          </div>

          {status === "Upcoming" && item.startTs && (
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                fontWeight: 700,
                color: "#38bdf8",
              }}
            >
              ⏳ Starts in: {String(d).padStart(2, "0")}d :{" "}
              {String(h).padStart(2, "0")}h : {String(m).padStart(2, "0")}m :{" "}
              {String(s).padStart(2, "0")}s
            </div>
          )}

          {status === "Open" && (
            <>
              <div
                style={{
                  marginTop: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#fbbf24",
                }}
              >
                ⏳ Remaining: {String(d).padStart(2, "0")}d :{" "}
                {String(h).padStart(2, "0")}h : {String(m).padStart(2, "0")}m :{" "}
                {String(s).padStart(2, "0")}s
              </div>
              {endingSoon && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#fb7185",
                  }}
                >
                  ⚡ Ending Soon
                </div>
              )}
              <div
                style={{
                  marginTop: 8,
                  width: "100%",
                  height: 10,
                  borderRadius: 999,
                  // Dark track is always full width (100%) and visibly different
                  // from the card background so you can see 100% length.
                  background: "#1f2937",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${remainingPercent}%`,
                    height: "100%",
                    borderRadius: 999,
                    background:
                      "linear-gradient(90deg,#10b981,#22d3ee)",
                  }}
                />
              </div>
            </>
          )}
        </div>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: statusColor,
          }}
        >
          {status}
        </span>
      </div>
      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        <button
          style={{
            borderRadius: 999,
            border: "1px solid #111827",
            background: "#020617",
            padding: "10px 24px",
            fontSize: 14,
            fontWeight: 600,
            color: "#f9fafb",
            cursor: "pointer",
          }}
          onClick={handleAuthRedirect}
        >
          View
        </button>
        {status === "Open" && (
          <button
            style={{
              borderRadius: 999,
              padding: "10px 28px",
              fontSize: 14,
              fontWeight: 800,
              background:
                "linear-gradient(135deg,#22c55e,#38bdf8)",
              color: "#020617",
              border: "none",
              cursor: "pointer",
            }}
            onClick={handleAuthRedirect}
          >
            Submit Bid
          </button>
        )}
      </div>
    </CardBase>
  );
}

function MarketplaceProductCard({ itm }) {
  return (
    <ProductCardRoot>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 56,
            height: 40,
            borderRadius: 10,
            background:
              "linear-gradient(135deg,#0ea5e9,#22c55e)",
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <strong
            style={{
              display: "block",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {itm.title}
          </strong>
          <div
            style={{
              fontSize: 12,
              color: "#94a3b8",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {itm.subtitle}
          </div>
        </div>
        <span
          style={{
            marginLeft: "auto",
            borderRadius: 999,
            border: "1px solid #1f2937",
            background: "#020617",
            padding: "2px 8px",
            fontSize: 11,
            color: "#94a3b8",
          }}
        >
          {itm.badge}
        </span>
      </div>
      <div
        style={{
          marginTop: 12,
          display: "flex",
          gap: 8,
        }}
      >
        <button
          style={{
            flex: 1,
            borderRadius: 8,
            border: "1px solid #1f2937",
            background: "#020617",
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 600,
            color: "#f9fafb",
            cursor: "pointer",
          }}
        >
          Buy Now
        </button>
        <button
          style={{
            flex: 1,
            borderRadius: 8,
            border: "1px solid #1f2937",
            background: "transparent",
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 600,
            color: "#cbd5f5",
            cursor: "pointer",
          }}
        >
          View Details
        </button>
      </div>
    </ProductCardRoot>
  );
}

// --------- Main component ----------

export const Marketplace = () => {
  const [showAllStores, setShowAllStores] = useState(false);
  const [showAllTenders, setShowAllTenders] = useState(false);
  const [auctions, setAuctions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isLoadingAuctions, setIsLoadingAuctions] = useState(false);
  const [rawStores, setRawStores] = useState([]);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [storeTotalCount, setStoreTotalCount] = useState(0);

  // Fetch dynamic auctions for "Open Tenders & Live Actions"
  useEffect(() => {
    const fetchAuctions = async () => {
      try {
        setIsLoadingAuctions(true);
        const response = await getAuctionList();

        let list = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          list = response.data.data;
        } else if (Array.isArray(response.data)) {
          list = response.data;
        }

        setAuctions(list);

        // Optional group data coming from the same endpoint (see auction-list response shape)
        if (Array.isArray(response.data?.group_data)) {
          setGroups(response.data.group_data);
        } else {
          setGroups([]);
        }
      } catch (err) {
        console.error("Error fetching auction-list for marketplace:", err);
        setAuctions([]);
      } finally {
        setIsLoadingAuctions(false);
      }
    };

    fetchAuctions();
  }, []);

  // Fetch dynamic reseller stores for "Reseller Stores" section
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setIsLoadingStores(true);
        // Request a high limit so the sidebar & "+More Stores" card can use ALL records
        const response = await getPublicSellerStores({
          page_number: 1,
          limit: 100,
        });

        let list = [];
        if (response.data?.data && Array.isArray(response.data.data)) {
          list = response.data.data;
        } else if (Array.isArray(response.data)) {
          list = response.data;
        }

        setRawStores(list);
        const total = response.data?.count;
        if (typeof total === "number") {
          setStoreTotalCount(total);
        } else {
          setStoreTotalCount(list.length);
        }
      } catch (err) {
        console.error("Error fetching public seller stores:", err);
        setRawStores([]);
        setStoreTotalCount(0);
      } finally {
        setIsLoadingStores(false);
      }
    };

    fetchStores();
  }, []);

  const auctionItems = useMemo(() => {
    if (!auctions || auctions.length === 0) return [];

    return auctions.map((a) => {
      const endTime = a.acution_end_time || a.auction_end_time;
      const startTime = a.acution_start_time || a.auction_start_time;

      const endDate = endTime ? new Date(endTime) : null;
      const startDate = startTime ? new Date(startTime) : null;

      const deadlineTs = endDate ? endDate.getTime() : Date.now();
      const startTs = startDate ? startDate.getTime() : null;

      const deadlineLabel = endDate
        ? endDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—";

      const title =
        a.product_info?.[0]?.title ||
        a.product_details?.[0]?.title ||
        a.description ||
        "Auction";

      const storeLabel =
        a.product_info?.[0]?.store_info?.[0]?.storeName ||
        a.product_info?.[0]?.store_name ||
        a.store_name ||
        "Store";

      // Price / budget from first auction tier or final_price
      const basePrice =
        (Array.isArray(a.auction_tier) && a.auction_tier[0]?.price) ||
        a.final_price ||
        0;

      // Total bids from aggregated bid array (public API shape)
      const totalBids =
        (Array.isArray(a.bid) && a.bid[0]?.total_bids) ||
        a.total_bids ||
        0;

      // Status is derived from auction_start_time + auction_end_time
      const nowTs = Date.now();
      const uiStatus =
        deadlineTs <= nowTs
          ? "Expired"
          : startTs && nowTs < startTs
          ? "Upcoming"
          : "Open";

      return {
        type: "auction",
        id: a._id,
        title,
        store: storeLabel,
        budget: basePrice ? `€ ${basePrice.toLocaleString("en-GB")}` : "—",
        deadline: deadlineLabel,
        deadlineTs,
        startTs,
        bids: totalBids,
        status: uiStatus,
      };
    });
  }, [auctions]);

  const groupItems = useMemo(() => {
    if (!groups || groups.length === 0) return [];

    return groups.map((g) => {
      const endTime = g.end_time;
      const startTime = g.start_time;

      const endDate = endTime ? new Date(endTime) : null;
      const startDate = startTime ? new Date(startTime) : null;

      const deadlineTs = endDate ? endDate.getTime() : Date.now();
      const startTs = startDate ? startDate.getTime() : null;

      const deadlineLabel = endDate
        ? endDate.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "—";

      const title =
        g.group_name ||
        g.product_details?.[0]?.title ||
        g.product?.[0]?.title ||
        "Group Purchase";

      const storeLabel =
        g.store?.[0]?.storeName ||
        g.store?.[0]?.store_name ||
        g.store_name ||
        "Store";

      // Use first product_details price as indicative budget
      const basePrice =
        (Array.isArray(g.product_details) && g.product_details[0]?.price) || 0;

      // Bids / joins: use aggregated buy_count.total_count when available,
      // otherwise fall back to invited_users length.
      const totalBuys =
        Array.isArray(g.buy_count) && g.buy_count[0]?.total_count
          ? g.buy_count[0].total_count
          : 0;

      const totalMembers = Array.isArray(g.invited_users)
        ? g.invited_users.length
        : 0;

      const bidsCount = totalBuys;

      const nowTs = Date.now();
      const uiStatus =
        deadlineTs <= nowTs
          ? "Expired"
          : startTs && nowTs < startTs
          ? "Upcoming"
          : "Open";

      return {
        type: "group",
        id: g._id,
        title,
        store: storeLabel,
        budget: basePrice ? `€ ${basePrice.toLocaleString("en-GB")}` : "—",
        deadline: deadlineLabel,
        deadlineTs,
        startTs,
        bids: bidsCount,
        status: uiStatus,
      };
    });
  }, [groups]);

  // All dynamic items (auctions + groups) for sidebar / counts
  const allTenderItems = useMemo(
    () => [...auctionItems, ...groupItems],
    [auctionItems, groupItems]
  );

  // Show up to 3 auctions + 3 groups in the main "Groups & Live Actions" grid
  const tenderItems = useMemo(() => {
    const firstAuctions = auctionItems.slice(0, 3);
    const firstGroups = groupItems.slice(0, 3);
    return [...firstAuctions, ...firstGroups];
  }, [auctionItems, groupItems]);

  const resellerItems = useMemo(() => {
    if (!rawStores || rawStores.length === 0) {
      if (storeTotalCount === 0) {
        setStoreTotalCount(fallbackResellerItems.length);
      }
      return fallbackResellerItems;
    }

    return rawStores.map((s) => {
      const name =
        s.storeName ||
        s.store_name ||
        s.group_name ||
        s.name ||
        "Store";

      const description =
        s.description ||
        s.storeDescription ||
        s.store_description ||
        "No description available.";

      // API fields:
      // total_product, total_group, active_total_group, total_auction, active_total_auction
      const products = s.total_product ?? s.total_products ?? 0;
      const activeAuctions = s.active_total_auction ?? 0;
      const groups = s.total_group ?? s.active_total_group ?? 0;
      const totalAuctions = s.total_auction ?? activeAuctions ?? 0;
      const totalGroups = s.total_group ?? groups ?? 0;

      return {
        name,
        description,
        products,
        activeAuctions,
        groups,
        totalAuctions,
        totalGroups,
      };
    });
  }, [rawStores, storeTotalCount]);

  return (
    <MarketplaceSection>
      <InnerContainer>
        <SectionTitle>Marketplace</SectionTitle>

        {/* Highlight: Open Tenders & Actions */}
        <HighlightCard>
          <HighlightHeader>
            <HighlightLabel>Goups &amp; Live Actions</HighlightLabel>
            <HighlightPill>Live Activity</HighlightPill>
          </HighlightHeader>

          <HighlightGrid>
            {tenderItems.length > 0
              ? tenderItems.slice(0, 6).map((t, i) => (
                  <TenderCard key={`t-${i}`} item={t} />
                ))
              : !isLoadingAuctions && (
                  <CardBase>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#94a3b8",
                      }}
                    >
                      No live Group available right now.
                    </div>
                  </CardBase>
                )}
            {allTenderItems.length > 6 && (
              <MoreTendersCard>
                <MoreTendersInner>
                  <MoreTendersNumber>
                    +{allTenderItems.length - 6}
                  </MoreTendersNumber>
                  <MoreTendersLabel>More Tenders</MoreTendersLabel>
                  <MoreTendersLink onClick={() => setShowAllTenders(true)}>
                    View All
                  </MoreTendersLink>
                </MoreTendersInner>
              </MoreTendersCard>
            )}
            {/* {actionItems
              .filter((a) => a.status === "Active")
              .map((a, i) => (
                <ActionCard key={`a-${i}`} item={a} />
              ))} */}
          </HighlightGrid>
        </HighlightCard>

        {/* All Tenders sidebar */}
        {showAllTenders && (
          <>
            <SidebarOverlay onClick={() => setShowAllTenders(false)} />
            <SidebarPanel>
              <SidebarHeader>
                <SidebarTitle>All Tenders</SidebarTitle>
                <SidebarClose onClick={() => setShowAllTenders(false)}>
                  ✕
                </SidebarClose>
              </SidebarHeader>
              <SidebarList>
                {allTenderItems.map((t, i) => {
                  const nowTs = Date.now();
                  const startTs = t.startTs || null;
                  const deadlineTs = t.deadlineTs || nowTs;

                  const totalDurationSeconds =
                    startTs && startTs < deadlineTs
                      ? Math.max(
                          1,
                          Math.floor((deadlineTs - startTs) / 1000)
                        )
                      : 3 * 24 * 60 * 60;

                  const remainingSeconds = Math.max(
                    0,
                    Math.floor((deadlineTs - nowTs) / 1000)
                  );

                  const d = Math.floor(remainingSeconds / 86400);
                  const h = Math.floor((remainingSeconds % 86400) / 3600);
                  const m = Math.floor((remainingSeconds % 3600) / 60);
                  const s = remainingSeconds % 60;

                  const remainingRatio =
                    totalDurationSeconds > 0
                      ? Math.max(
                          0,
                          Math.min(1, remainingSeconds / totalDurationSeconds)
                        )
                      : 0;
                  const remainingPercent = remainingRatio * 100;

                  const startLabel = startTs
                    ? new Date(startTs).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "—";

                  const endLabel = deadlineTs
                    ? new Date(deadlineTs).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : t.deadline || "—";

                  const isExpired =
                    (t.status && t.status === "Expired") ||
                    remainingSeconds <= 0;
                  const statusLabel = isExpired
                    ? "Expired"
                    : t.status || "";
                  const statusColor = isExpired ? "#fb7185" : "#34d399";

                  return (
                    <CardBase key={i}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {t.title}
                        </div>
                        {statusLabel && (
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: statusColor,
                              textTransform: "uppercase",
                              letterSpacing: 0.4,
                            }}
                          >
                            {statusLabel}
                          </span>
                        )}
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}
                      >
                        Store: {t.store}
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        Budget: {t.budget}
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        Starts: {startLabel}
                      </div>
                      <div style={{ fontSize: 12, color: "#94a3b8" }}>
                        Ends: {endLabel}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 12,
                          fontWeight: 700,
                          color: "#fbbf24",
                        }}
                      >
                        Bids: {t.bids}
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 11,
                          color: "#e5e7eb",
                        }}
                      >
                        Remaining: {String(d).padStart(2, "0")}d :{" "}
                        {String(h).padStart(2, "0")}h :{" "}
                        {String(m).padStart(2, "0")}m :{" "}
                        {String(s).padStart(2, "0")}s
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          width: "100%",
                          height: 8,
                          borderRadius: 999,
                          background: "#1f2937",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${remainingPercent}%`,
                            height: "100%",
                            borderRadius: 999,
                            background:
                              "linear-gradient(90deg,#10b981,#22d3ee)",
                          }}
                        />
                      </div>
                    </CardBase>
                  );
                })}
              </SidebarList>
            </SidebarPanel>
          </>
        )}

        {/* Reseller Stores */}
        <ResellerSection>
          <ResellerTitle>Reseller Stores</ResellerTitle>
          <ResellerGrid>
            {resellerItems.slice(0, 6).map((r, i) => (
              <ResellerCard key={i} item={r} />
            ))}

            {storeTotalCount > resellerItems.slice(0, 6).length && (
              <MoreStoresCard>
                <MoreStoresInner>
                  <MoreStoresNumber>
                    +{storeTotalCount - resellerItems.slice(0, 6).length}
                  </MoreStoresNumber>
                  <MoreStoresLabel>More Stores</MoreStoresLabel>
                  <MoreStoresLink onClick={() => setShowAllStores(true)}>
                    View All
                  </MoreStoresLink>
                </MoreStoresInner>
              </MoreStoresCard>
            )}
          </ResellerGrid>
        </ResellerSection>

        {/* Stores sidebar */}
        {showAllStores && (
          <>
            <SidebarOverlay onClick={() => setShowAllStores(false)} />
            <SidebarPanel>
              <SidebarHeader>
                <SidebarTitle>All Stores</SidebarTitle>
                <SidebarClose onClick={() => setShowAllStores(false)}>
                  ✕
                </SidebarClose>
              </SidebarHeader>
              <SidebarList>
                {resellerItems.map((store, i) => (
                  <SidebarStoreCard key={i}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>
                      {store.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#94a3b8",
                        marginTop: 4,
                      }}
                    >
                      {store.description}
                    </div>
                    <ResellerStats style={{ marginTop: 12 }}>
                      <ResellerStatBox>
                        <StatValue>{store.products}</StatValue>
                        <StatLabel>Products</StatLabel>
                      </ResellerStatBox>
                      <ResellerStatBox>
                        <StatValue>{store.activeAuctions}</StatValue>
                        <StatLabel>Auctions</StatLabel>
                      </ResellerStatBox>
                      <ResellerStatBox>
                        <StatValue>{store.groups}</StatValue>
                        <StatLabel>Groups</StatLabel>
                      </ResellerStatBox>
                    </ResellerStats>
                  </SidebarStoreCard>
                ))}
              </SidebarList>
            </SidebarPanel>
          </>
        )}

        {/* Products */}
        {/* <ProductsSection>
          <ProductsTitle>Products</ProductsTitle>
          <ProductsGrid>
            {marketplaceItems.map((itm, i) => (
              <MarketplaceProductCard key={i} itm={itm} />
            ))}
          </ProductsGrid>
        </ProductsSection> */}
      </InnerContainer>
    </MarketplaceSection>
  );
};

export default Marketplace;

